<script>
	/* eslint-disable svelte/no-at-html-tags */
	// NPM Modules
	import dayjs from 'dayjs';
	import collect from 'collect.js';
	import Link from 'svelte-navaid/Link.svelte';
	import { createEventDispatcher, tick } from 'svelte';
	import relativeTime from 'dayjs/plugin/relativeTime';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	// Common
	import {
		getBetLiabilityForDisplay,
		getBetNetWinAmountForDisplay,
		getBetOddsForDisplay,
		getBetWinAmountForDisplay,
		isBonusBet,
		isBoostedBet,
		isBoxedBet,
		isCashedOutBet,
		isExoticBet,
		isLiveBet,
		isMultiBet,
		isRacingBet,
		isSGMBet,
		isSGMLeg,
		isSportBet,
		isSportSinglesBet,
		isSportLeg,
		isSRMBet,
		isVirtualLeg,
		hasPromotionOffer,
		getLegCashoutOddsForDisplay,
		getBetCashoutOddsForDisplay,
		isRacingSinglesBet
	} from '@/Common/Bet.js';
	// Constants
	import { BetStatus } from '@/Constants/Bet';
	import { ActionType } from '@/Constants/Rule.js';
	import { UserRatingColours } from '@/Constants/User.js';
	// Partials
	import StatusAdjustment from '@/Partials/Bets/_StatusAdjustment.svelte';
	import BetExtraInfo from '@/Partials/Bets/_BetExtraInfo.svelte';
	import ResultBet from '@/Partials/Bets/_ResultBet.svelte';
	import SGMBetDisplay from '@/Partials/Bets/_SGMBetDisplay.svelte';
	import SRMBetDisplay from '@/Partials/Bets/_SRMBetDisplay.svelte';
	import SportBetDisplay from '@/Partials/Bets/_SportBetDisplay.svelte';
	import RacingBetDisplay from '@/Partials/Bets/_RacingBetDisplay.svelte';
	import ExoticsBetDisplay from '@/Partials/Bets/_ExoticsBetDisplay.svelte';
	import LegOddsDisplay from '@/Partials/Bets/_LegOddsDisplay.svelte';
	import MultiLegStatusDisplay from '@/Partials/Bets/_MultiLegStatusDisplay.svelte';
	import BetTickerCashoutActions from '@/Partials/Bets/_BetTickerCashoutActions.svelte';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import AnimateChange from '@/Components/AnimateChange.svelte';
	// Services
	import { CurrencyFormat } from '@/Services/Helpers';
	import { getIconForSelection, routeForMeetingRace, routeForSportEvent } from '@/Services/Helpers.js';
	// Stores
	import ACLStore from '@/Stores/ACLStore.js';

	const dispatch = createEventDispatcher();

	dayjs.extend(relativeTime);
	dayjs.extend(localizedFormat);

	// exports/props
	export let bet;
	export let betTicker = true;
	export let reviewPayout = false;
	export let reviewCashout = false;
	export let cashoutFormat = false;
	export let manualResult;

	let showBetInfo = false;
	let showBetLegResult = false;

	// GET PRIMARY LEG SELECTION
	$: selection = bet && bet.legs.data.first().leg_selections.data.first();

	// MULTI BETS
	let multiLegs;
	$: bet && isMultiBet(bet) && setupMultiBet();

	function setupMultiBet() {
		let legs = collect([]);
		bet.legs.data.each((leg) => {
			let selection = leg.leg_selections.data.first();
			let legData = {
				id: leg.id,
				selection_id: selection.id,
				number: selection.number,
				name: selection.name,
				sport: selection.sport,
				sub_sport: selection.sub_sport,
				event: (selection.competition && selection.competition.name) || '-',
				sport_event: (selection.event && selection.event.name) || '-',
				event_number: (selection.event && selection.event.number) || '-',
				route_event: selection.event,
				route_meeting: selection.competition || null,
				bet_type: leg.bet_type,
				is_each_way: leg.is_each_way,
				is_boxed: leg.is_boxed,
				is_virtual: leg.is_virtual,
				status: leg.status,
				product: leg.product,
				fixed_price: leg.fixed_price,
				cashout_fixed_price: leg.cashout_fixed_price,
				leg_number: leg.leg_number,
				selection_status: selection.status
			};

			if (isSGMLeg(leg)) {
				legData['selection'] = selection;
			}

			if (selection.market) {
				legData['market'] = selection.market;
			}

			legs.push(legData);
		});
		multiLegs = legs;
	}

	async function openBetExtraInfo() {
		showBetInfo = true;
		await tick();
		window.scrollTo(0, 0);
	}

	async function openBetLegResult() {
		showBetLegResult = true;
		await tick();
		window.scrollTo(0, 0);
	}

	// Used for bet review payout
	function actualWinAmount(bet) {
		let rule = bet.rules.data.firstWhere('rule_meta.type.id', ActionType.Result);
		return rule?.bet_meta ? CurrencyFormat(rule.bet_meta.original_win_amount) : 'N/A';
	}
</script>

<td class="p-2 font-semibold border-t">
	<div class="flex justify-center items-center whitespace-nowrap">
		<Link class="hover:underline" href="/bet-ticker?ticket={bet.ticket}" target="_blank" title="Open just this bet">
			#{bet.ticket}
		</Link>
	</div>
</td>
<td class="border-t w-24">
	<div class="flex flex-col justify-center items-center text-xs flex-nowrap">
		<div>{dayjs(bet.created_at).format('ddd D MMM YYYY')}</div>
		<div>{dayjs(bet.created_at).format('HH:mm:ss Z')}</div>
	</div>
</td>
<td
	class="p-2 text-xs text-center border-t {UserRatingColours[bet.user.rating.id]?.bg ?? 'bg-gray-300'}
    {UserRatingColours[bet.user.rating.id]?.text ?? 'text-dark'}"
>
	<div class="flex flex-col items-center justify-center space-y-1">
		<Link href="/users/{bet.user.id}" class="focus:outline-none hover:underline">
			<div>{bet.user.first_name} {bet.user.last_name} - {bet.user.name}</div>
		</Link>
		<span>{bet.user.rating.name} {bet.user.sub_rating ? ' (' + bet.user.sub_rating.name + ') ' : ''}</span>
	</div>
</td>
<td class="p-2 border-t text-sm">
	<div class="flex flex-col justify-start space-y-1 flex-nowrap">
		<!-- EVENT -->
		{#if isSportBet(bet)}
			<div class="flex flex-row">
				<Link href={routeForSportEvent(selection.event)} class="focus:outline-none hover:underline" target="_blank">
					<div class="flex flex-row">
						<span
							>{(selection.event && selection.event.name) || '-'} ({(selection.competition &&
								selection.competition.name) ||
								'-'})
						</span>
						<div title={selection.sport.name} class="ml-1">
							<FontIcon icon={getIconForSelection(selection)} />
						</div>
					</div>
				</Link>
			</div>
		{:else if isMultiBet(bet)}
			{#each multiLegs && multiLegs.all() as leg (leg.id)}
				{#if !isVirtualLeg(leg)}
					{#if isSportLeg(leg) || isSGMLeg(leg)}
						<div class="flex flex-row space-x-1">
							<Link
								href={routeForSportEvent(leg.route_event)}
								class="focus:outline-none hover:underline"
								target="_blank"
							>
								<div class="flex flex-row w-full">
									<span class="font-semibold w-7">L{leg.leg_number}:</span>
									<span>{leg.sport_event} ({leg.route_meeting.name})</span>
									<div title={leg.sport.name} class="ml-1">
										<FontIcon icon={getIconForSelection(leg)} />
									</div>
								</div>
							</Link>
						</div>
					{:else}
						<Link
							href={routeForMeetingRace(leg.route_meeting, leg.route_event)}
							class="focus:outline-none hover:underline"
							target="_blank"
						>
							<div class="flex flex-row w-full">
								<span class="font-semibold w-7">L{leg.leg_number}:</span>
								<span>{leg.event} - Race {leg.event_number}</span>
								<div title={leg.sport.name} class="ml-1">
									<FontIcon icon={getIconForSelection(leg)} className="mr-1" />
								</div>
							</div>
						</Link>
					{/if}
				{/if}
			{/each}
		{:else if isRacingBet(bet)}
			<Link
				href={routeForMeetingRace(selection.competition, selection.event)}
				class="focus:outline-none hover:underline"
				target="_blank"
			>
				<div class="flex flex-row">
					<span>
						{selection.competition.name} - R{selection.event.number}
					</span>
					<div title={selection.sport.name} class="ml-1">
						<FontIcon icon={getIconForSelection(selection)} className="mr-1" />
					</div>
				</div>
			</Link>
		{/if}
	</div>
</td>
<td class="px-2 py-1.5 border-t group">
	<!-- SELECTION -->
	<div class="flex flex-row w-full justify-between">
		<div class="flex flex-col text-sm">
			<div class="flex flex-row items-start space-x-1.5 mb-0.5">
				<div class="font-semibold">
					{(isBoxedBet(bet) ? 'Boxed ' : '') + bet.bet_type.name}
				</div>
				{#if isLiveBet(bet)}
					<div class="flex flex-row text-center uppercase rounded text-sm" style="font-size: .8rem;">
						<FontIcon icon={'live-event'} className="text-secondary rounded-sm" />
					</div>
				{/if}
			</div>
			<div class="flex flex-row">
				{#if isSportSinglesBet(bet)}
					<SportBetDisplay {bet} {selection} {manualResult} />
				{:else if isSGMBet(bet)}
					<SGMBetDisplay {selection} {manualResult} />
				{:else if isRacingSinglesBet(bet)}
					<RacingBetDisplay {bet} {selection} />
					<LegOddsDisplay {bet} selection={bet.legs.data.first()} showGuided={true} />
				{:else if isExoticBet(bet)}
					<ExoticsBetDisplay {bet} fancyDisplay={false} />
				{:else if isSRMBet(bet)}
					<SRMBetDisplay {bet} />
				{:else if isMultiBet(bet)}
					<div class="flex flex-col">
						{#each multiLegs && multiLegs.all() as leg (leg.id)}
							{#if !isVirtualLeg(leg)}
								<div class="flex flex-row items-center space-x-1 mb-0.5">
									<MultiLegStatusDisplay {leg} {multiLegs} />
									<div class="flex flex-row">
										{#if isSportLeg(leg)}
											<SportBetDisplay {bet} selection={leg} {manualResult} inMulti={true} />
										{:else if isSGMLeg(leg)}
											<SGMBetDisplay selection={leg} {manualResult} inMulti={true} />
										{:else}
											<RacingBetDisplay {bet} selection={leg} inMulti={true} />
										{/if}
									</div>
									<LegOddsDisplay {bet} selection={leg} showGuided={true} inMulti={true} />
								</div>
							{/if}
						{/each}
					</div>
				{/if}
			</div>
		</div>
		<div class="w-4 my-auto">
			{#if $ACLStore && ACLStore.granted('bets.update') && bet.status.id === BetStatus.Active && ((isSportBet(bet) && !isSGMBet(bet)) || isMultiBet(bet))}
				<button
					on:click={() => openBetLegResult()}
					class="flex items-center justify-center w-full space-x-1 rounded-md focus:outline-none btn p-0"
				>
					<Icon name="pencil" classes="w-4 h-4 hidden group-hover:block group-hover:text-orange-500" />
				</button>
			{/if}
		</div>
	</div>
</td>
<td class=" px-2 border-t">
	<!-- BET -->
	<div class="flex flex-col">
		<div class="flex flex-row items-center text-nowrap">
			{#if isBonusBet(bet)}
				<div class="flex flex-row whitespace-nowrap justify-center items-center mr-0.5">
					<FontIcon icon={'gift'} className="text-white bg-gray-500 p-1 rounded-sm text-tiny h-4" />
				</div>
			{/if}
			<div class="flex flex-row whitespace-nowrap justify-center items-center">
				<AnimateChange value={CurrencyFormat(bet.bet_amount)} />
			</div>
			<div class="mx-0.5 pb-0.8 flex flex-row whitespace-nowrap justify-center items-center">@</div>
			<div class="flex flex-row whitespace-nowrap justify-center items-center">
				{getBetOddsForDisplay(bet)}
			</div>
			<div class="ml-0.8 flex flex-row whitespace-nowrap justify-center items-center">
				{#if isBoostedBet(bet)}
					<FontIcon icon="boost-up" className="text-primary" />
				{/if}
				{#if isCashedOutBet(bet)}
					<FontIcon icon="cashout" className="text-primary" />
				{/if}
				{#if hasPromotionOffer(bet)}
					<FontIcon icon="bonus-back" className="text-primary" />
				{/if}
			</div>
		</div>
		{#if bet.is_partially_accepted}
			<div class="text-sm text-danger">
				(Partialled from: {CurrencyFormat(bet.requested_bet_amount)})
			</div>
		{/if}
	</div>
</td>
{#if betTicker}
	<td class="p-2 border-t">{getBetLiabilityForDisplay(bet)}</td>
	<td class="p-2 border-t">{getBetWinAmountForDisplay(bet)}</td>
	{#if cashoutFormat}
		<td class="p-2 border-t">
			<div class="flex flex-col justify-center items-start text-xs flex-nowrap">
				<div>{dayjs(bet.resulted_at).format('ddd D MMM YYYY')}</div>
				<div>{dayjs(bet.resulted_at).format('HH:mm:ss Z')}</div>
			</div>
		</td>
		<td class="p-2 border-t text-danger">
			{#if isMultiBet(bet)}
				{#each multiLegs && multiLegs.all() as leg}
					{#if !isVirtualLeg(leg)}
						<div class="flex flex-row items-center space-x-1 text-sm text-dark">
							<MultiLegStatusDisplay {leg} {multiLegs} showStatus={false} />
							<div class="-pb-1 text-danger flex flex-row whitespace-nowrap">
								{getLegCashoutOddsForDisplay(leg, bet)}
							</div>
						</div>
					{/if}
				{/each}
			{:else}
				{getBetCashoutOddsForDisplay(bet)}
			{/if}
		</td>
	{/if}
	<td class="p-2 border-t">{getBetNetWinAmountForDisplay(bet)}</td>
	<td class="p-2 text-center border-t"><StatusAdjustment {bet} /></td>
	<td class="p-2 border-t">
		<button on:click={openBetExtraInfo} class="outline-none focus:outline-none">
			<Icon name="info" classes="w-4 h-4 text-blue-500" />
		</button>
	</td>
	{#if showBetInfo}
		<BetExtraInfo bind:open={showBetInfo} bind:bet />
	{/if}
	{#if showBetLegResult}
		<ResultBet bind:open={showBetLegResult} bind:bet on:updated={() => dispatch('updated')} />
	{/if}
{/if}
{#if reviewCashout}
	<td class="p-2 border-t">{bet.win_amount ? `${CurrencyFormat(bet.win_amount)}` : '-'}</td>
	<td class="p-2 border-t">
		<BetTickerCashoutActions {bet} />
	</td>
{/if}
{#if reviewPayout}
	<td class="p-2 text-center border-t">{actualWinAmount(bet)}</td>
	<td class="p-2 text-center border-t">
		{bet.win_amount ? `${CurrencyFormat(bet.win_amount)}` : '-'}
	</td>
	<td class="p-2 border-t">
		<StatusAdjustment {bet} />
	</td>
{/if}
